import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, allArticles } from '@/mocks/data';

const breadcrumbSchema = (catName: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ntrmaster.com' },
    { '@type': 'ListItem', position: 2, name: catName },
  ],
});

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const activeCat = categories.find((c) => c.slug === activeCategory) || categories[0];
  const articles = allArticles.filter((a) => a.category === activeCategory);

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} | NTR大师`}
        description={currentCategory.description}
        keywords={`NTR大师,${currentCategory.name},NTR内容指南,NTR创作与鉴赏`}
        canonicalUrl={`https://ntrmaster.com/category/${currentCategory.slug}`}
        schemaData={breadcrumbSchema(currentCategory.name)}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <div className="pt-24 pb-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: activeCat.name },
            ]}
          />
        </div>

        <header className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-10">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
            {activeCat.name}
          </h1>
          <p className="mt-2 text-foreground-400 text-sm">{activeCat.description}</p>
        </header>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-950'
                    : 'bg-background-900 text-foreground-400 hover:text-foreground-200 hover:bg-background-800 border border-background-800'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <main className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-900 rounded-lg overflow-hidden border border-background-800 hover:border-primary-400/20 transition-all cursor-pointer"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - NTR大师`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h2 className="text-base font-semibold text-foreground-200 group-hover:text-primary-300 transition-colors mb-2 font-heading line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-600">
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {articles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-900">
                <i className="ri-file-list-3-line text-2xl text-foreground-600"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}